import { apiClient, ApiError, ApiEnvelope, fetchCsrfCookie } from './client';
import { endpoints } from './endpoints';
import type { Country, Profile, ProfileDetail } from './types/common';

export interface LoginOtpPayload {
  country_code: string;
  mobile_no: string;
}
export interface LoginPayload extends LoginOtpPayload {
  password: string; // OTP
  firebase_token?: string;
}

// Shape of a single role-detail record (snake_case as returned by the backend).
// Fields vary by role; keep loose and pull what we need.
interface RoleDetailRaw {
  id: number;
  name?: string;
  profile_pic?: string;
  is_active?: number;
  academy?: {
    id?: number;
    academy_name?: string;
    country?: { id?: number; name?: string; calling_code?: string } | null;
    /** True when the academy completed Cashfree onboarding. Describes the
     *  payload only — the fees UI gates on each invoice's `shareable_link`,
     *  which the backend derives from this same condition but re-sends with
     *  every response rather than caching it here. See
     *  `pages/admin/fees/paymentLink.ts`. */
    is_academy_onboarded_to_cashfree?: 0 | 1 | boolean;
    [k: string]: unknown;
  };
  branches?: Array<{ id?: number; name?: string }>;
  [k: string]: unknown;
}

// Shape of the user object returned by /login and /profile-list.
// admin_detail is a single object (or null); the others are arrays.
export interface UserWithDetails {
  id: number;
  name?: string;
  mobile_no?: string;
  country_code?: string;
  app_platform?: string;
  is_active?: number;
  admin_detail?: RoleDetailRaw | null;
  trainer_detail?: RoleDetailRaw[] | null;
  manager_detail?: RoleDetailRaw[] | null;
  student_detail?: RoleDetailRaw[] | null;
  [k: string]: unknown;
}

interface LoginRawResponse {
  status: 0 | 1;
  message?: string;
  token?: string;
  user?: UserWithDetails;
}

interface ProfileListRawResponse {
  status: 0 | 1;
  message?: string;
  user?: UserWithDetails;
}

/** Build the list of selectable profiles from a user object. */
export function profilesFromUser(u: UserWithDetails | undefined | null): Profile[] {
  if (!u) return [];
  const profiles: Profile[] = [];

  if (u.admin_detail) {
    const d = u.admin_detail;
    profiles.push({
      id: d.id,
      user_id: u.id,
      role: 'admin',
      name: d.name ?? u.name,
      academy_name: d.academy?.academy_name,
      country_code: d.academy?.country?.calling_code,
      academy_id: d.academy?.id,
      academy_type_id: (d.academy as { academy_type_id?: number } | undefined)?.academy_type_id,
      profile_pic: d.profile_pic,
      detail: d as ProfileDetail,
    } as Profile);
  }
  for (const d of u.manager_detail ?? []) {
    profiles.push({
      id: d.id,
      user_id: u.id,
      role: 'manager',
      name: d.name ?? u.name,
      academy_name: d.academy?.academy_name,
      country_code: d.academy?.country?.calling_code,
      academy_id: d.academy?.id,
      academy_type_id: (d.academy as { academy_type_id?: number } | undefined)?.academy_type_id,
      profile_pic: d.profile_pic,
      detail: d as ProfileDetail,
    } as Profile);
  }
  for (const d of u.trainer_detail ?? []) {
    profiles.push({
      id: d.id,
      user_id: u.id,
      role: 'trainer',
      name: d.name ?? u.name,
      academy_name: d.academy?.academy_name,
      branch_name: d.branches?.[0]?.name,
      country_code: d.academy?.country?.calling_code,
      academy_id: d.academy?.id,
      academy_type_id: (d.academy as { academy_type_id?: number } | undefined)?.academy_type_id,
      profile_pic: d.profile_pic,
      detail: d as ProfileDetail,
    } as Profile);
  }
  for (const d of u.student_detail ?? []) {
    profiles.push({
      id: d.id,
      user_id: u.id,
      role: 'student',
      name: d.name ?? u.name,
      academy_name: d.academy?.academy_name,
      academy_id: d.academy?.id,
      academy_type_id: (d.academy as { academy_type_id?: number } | undefined)?.academy_type_id,
      profile_pic: d.profile_pic,
      detail: d as ProfileDetail,
    } as Profile);
  }
  return profiles;
}

// /api/login-otp returns { status, message, tester } — no data.
export async function requestLoginOtp(payload: LoginOtpPayload): Promise<{ tester: boolean }> {
  const res = await apiClient.post<ApiEnvelope<unknown> & { tester?: boolean }>(
    endpoints.loginOtp,
    payload,
  );
  if (res.data.status === 0) {
    throw new ApiError(res.data.message || 'Failed to send OTP', res.status, res.data);
  }
  return { tester: Boolean(res.data.tester) };
}

// /api/login returns { status, message, user, token? }.
// On the web SPA, the backend uses session-cookie auth and OMITS `token` from
// the body — the XSS-resistant cookie is what authenticates subsequent calls.
// `token` is therefore optional in the result; legacy callers should not rely
// on it on the web.
export interface VerifyOtpResult {
  token?: string;
  user: UserWithDetails;
  profiles: Profile[];
}
export async function verifyLoginOtp(payload: LoginPayload): Promise<VerifyOtpResult> {
  // Sanctum SPA prelude: prime the XSRF-TOKEN cookie before posting login so
  // the request carries a valid CSRF header. No-op for the mobile client (it
  // doesn't go through this code path) and harmless to call repeatedly.
  await fetchCsrfCookie();

  const res = await apiClient.post<LoginRawResponse>(endpoints.login, payload);
  if (res.data.status === 0) {
    throw new ApiError(res.data.message || 'Login failed', res.status, res.data);
  }
  if (!res.data.user) {
    throw new ApiError('Login response missing user', res.status, res.data);
  }
  return {
    token: res.data.token,
    user: res.data.user,
    profiles: profilesFromUser(res.data.user),
  };
}

// Still useful for refreshing the user/profile list after login (e.g. on app
// reload). Same shape as /login's user payload.
export async function fetchProfileList(): Promise<{ user: UserWithDetails | null; profiles: Profile[] }> {
  const res = await apiClient.get<ProfileListRawResponse>(endpoints.profileList);
  if (res.data.status === 0) {
    throw new ApiError(res.data.message || 'Failed to load profiles', res.status, res.data);
  }
  const user = res.data.user ?? null;
  return { user, profiles: profilesFromUser(user) };
}

export async function logout(): Promise<void> {
  try {
    await apiClient.post(endpoints.logout);
  } catch {
    // Token revocation is best-effort; clear local state regardless.
  }
}

export async function fetchCountries(): Promise<Country[]> {
  const res = await apiClient.get<ApiEnvelope<Country[]>>(endpoints.countries);
  return (res.data.data ?? []) as Country[];
}
